import React, { useEffect, useMemo, useRef, useState } from "react";
import { Edit, useForm, useSelect } from "@refinedev/antd";
import { useNavigation } from "@refinedev/core";
import {
  Form,
  Input,
  DatePicker,
  InputNumber,
  Select,
  App,
  Steps,
  Space,
  Button,
  Typography,
  Divider,
  Alert,
  Card,
  Row,
  Col,
} from "antd";
import dayjs from "dayjs";
import { VideoUpload, PosterUpload } from "../../components/uploads";
import { supabaseClient } from "../../utility/supabaseClient";

const { Title, Paragraph, Text } = Typography;

const STEP_ITEMS = [
  { title: "基础信息" },
  { title: "封面与视频" },
];

export const MovieEdit: React.FC = () => {
  const [form] = Form.useForm();
  const { formProps, queryResult } = useForm({
    redirect: false,
    meta: { select: "*, movie_categories(category_id)" },
  });
  const { selectProps: categorySelectProps } = useSelect({
    resource: "categories",
    optionLabel: "name",
    optionValue: "id",
    pagination: { mode: "off" },
  });
  const { message } = App.useApp();

  const [currentStep, setCurrentStep] = useState(0);
  const [savingBase, setSavingBase] = useState(false);
  const [savingMedia, setSavingMedia] = useState(false);
  const [posterUrl, setPosterUrl] = useState<string | undefined>();
  const [backdropUrl, setBackdropUrl] = useState<string | undefined>();
  const [videoUid, setVideoUid] = useState<string | undefined>();
  const mediaRef = useRef<HTMLDivElement | null>(null);
  const { list } = useNavigation();

  const record = queryResult?.data?.data as any;
  const movieId = record?.id as string | undefined;

  // 初始化表单与媒资预览
  useEffect(() => {
    if (record) {
      form.setFieldsValue({
        ...record,
        category_ids: (record.movie_categories || []).map(
          (item: any) => item.category_id
        ),
      });
      setPosterUrl(record.poster_url || undefined);
      setBackdropUrl(record.backdrop_url || undefined);
      setCurrentStep(0);
    }
  }, [record, form]);

  const handleStepChange = (next: number) => {
    if (next === 1 && !movieId) {
      message.warning("请先保存基础信息并生成影片ID");
      setCurrentStep(0);
      return;
    }
    setCurrentStep(next);
    if (next === 1) {
      window.requestAnimationFrame(() => {
        mediaRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  };

  const handleSaveBase = async () => {
    let values: any;
    try {
      values = await form.validateFields();
    } catch (err) {
      message.warning("请完善必填项后再保存");
      return;
    }
    setSavingBase(true);
    const { category_ids = [], release_date, ...payload } = values;
    const normalizedPayload = {
      ...payload,
      release_date: release_date ? dayjs(release_date).format("YYYY-MM-DD") : null,
    };

    try {
      if (!movieId) {
        message.error("未找到影片ID，无法保存");
        return;
      }

      const { error: updateError } = await supabaseClient
        .from("movies")
        .update(normalizedPayload)
        .eq("id", movieId);
      if (updateError) throw updateError;

      // 重建分类关联
      const { error: delError } = await supabaseClient
        .from("movie_categories")
        .delete()
        .eq("movie_id", movieId);
      if (delError) throw delError;

      if (category_ids.length > 0) {
        const { error: addError } = await supabaseClient
          .from("movie_categories")
          .insert(
            category_ids.map((id: string) => ({
              movie_id: movieId,
              category_id: id,
            }))
          );
        if (addError) throw addError;
      }

      message.success("基础信息已保存，继续上传封面与视频");
      setCurrentStep(1);
    } catch (err: any) {
      console.error("更新电影失败:", err);
      message.error(err?.message || "保存失败，请重试");
    } finally {
      setSavingBase(false);
    }
  };

  const handleSaveMedia = async () => {
    if (!movieId) {
      message.warning("请先保存基础信息，生成影片ID后再上传媒资");
      setCurrentStep(0);
      return;
    }
    if (!posterUrl) {
      message.error("请先上传列表封面（16:9），再提交");
      return;
    }
    if (!videoUid) {
      message.error("请先上传视频后再提交");
      return;
    }
    setSavingMedia(true);
    try {
      const { error } = await supabaseClient
        .from("movies")
        .update({
          poster_url: posterUrl || null,
          backdrop_url: backdropUrl || null,
        })
        .eq("id", movieId);
      if (error) throw error;
      message.success("封面已保存，视频上传完成后即可生效");
      // 重置脏状态，避免离开提示
      form.resetFields();
      list("movies");
    } catch (err: any) {
      console.error("保存媒资失败:", err);
      message.error(err?.message || "保存媒资失败，请重试");
    } finally {
      setSavingMedia(false);
    }
  };

  const idTip = useMemo(
    () =>
      movieId
        ? `影片ID：${movieId.slice(0, 8)}...`
        : "加载中或未找到影片ID",
    [movieId]
  );

  return (
    <Edit
      headerButtons={() => null}
      footerButtons={() => null}
      contentProps={{ variant: "borderless" }}
      isLoading={queryResult?.isLoading}
    >
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <div>
          <Title level={4} style={{ marginBottom: 12 }}>
            影片编辑分步
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 8 }}>
            第一步：完善基础信息与分类；第二步：上传横版16:9封面与视频，保持与 APP 英雄图比例一致。
          </Paragraph>
          <Steps
            current={currentStep}
            onChange={handleStepChange}
            items={STEP_ITEMS}
            style={{ width: "100%", maxWidth: 1100, margin: "0 auto" }}
            labelPlacement="vertical"
          />
        </div>

        <Divider style={{ margin: "12px 0" }} />

        <Form
          {...formProps}
          form={form}
          layout="vertical"
          style={{ width: "100%", rowGap: 12 }}
        >
          {currentStep === 0 && (
            <Space direction="vertical" style={{ width: "100%" }} size="middle">
              <Card title="基础信息" bordered={false} bodyStyle={{ paddingTop: 12, paddingBottom: 8 }}>
                <Row gutter={16}>
                  <Col span={24}>
                    <Form.Item label="ID" name="id">
                      <Input disabled />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="标题"
                      name="title"
                      rules={[{ required: true, message: "请输入电影标题" }]}
                    >
                      <Input placeholder="示例：盗梦空间" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item label="原标题" name="original_title">
                      <Input placeholder="原版片名（可选）" />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      label="简介"
                      name="overview"
                      rules={[{ required: true, message: "请填写剧情简介" }]}
                    >
                      <Input.TextArea rows={4} placeholder="一句话介绍与看点" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item label="标语" name="tagline">
                      <Input placeholder="营销标语（可选）" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="上映日期"
                      name="release_date"
                      rules={[{ required: true, message: "请选择上映日期" }]}
                      getValueProps={(value) => ({
                        value: value ? dayjs(value) : undefined,
                      })}
                    >
                      <DatePicker style={{ width: "100%" }} placeholder="选择日期" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="时长（分钟）"
                      name="runtime"
                      rules={[{ required: true, message: "请输入影片时长" }]}
                    >
                      <InputNumber min={1} style={{ width: "100%" }} placeholder="如：120" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="语言"
                      name="language"
                      rules={[{ required: true, message: "请输入语言编码" }]}
                    >
                      <Input placeholder="如：zh-CN / en" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="状态"
                      name="status"
                      rules={[{ required: true, message: "请选择影片状态" }]}
                    >
                      <Select placeholder="请选择">
                        <Select.Option value="draft">草稿</Select.Option>
                        <Select.Option value="released">已发布</Select.Option>
                        <Select.Option value="archived">已归档</Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="所属分类"
                      name="category_ids"
                      tooltip="支持多选，保存后自动写入 movie_categories 关联表"
                      rules={[{ required: true, message: "请选择至少一个分类" }]}
                    >
                      <Select
                        mode="multiple"
                        placeholder="选择分类（可多选）"
                        {...categorySelectProps}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>

              <Card title="数据指标" bordered={false} bodyStyle={{ paddingTop: 12, paddingBottom: 8 }}>
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item label="评分" name="vote_average">
                      <InputNumber min={0} max={10} step={0.1} style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item label="投票数" name="vote_count">
                      <InputNumber min={0} style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item label="人气" name="popularity">
                      <InputNumber min={0} style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item label="观看次数" name="view_count">
                      <InputNumber min={0} style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
                <Text type="secondary" style={{ alignSelf: "center" }}>{idTip}</Text>
                <Button type="primary" onClick={handleSaveBase} loading={savingBase}>
                  保存基础信息并下一步
                </Button>
              </div>
            </Space>
          )}

          {currentStep === 1 && (
            <Space direction="vertical" style={{ width: "100%" }} size="middle" ref={mediaRef}>
              {!movieId && (
                <Alert
                  type="info"
                  showIcon
                  message="请先保存基础信息，生成影片ID后再上传封面与视频"
                />
              )}

              <Card
                title="封面与背景"
                bordered={false}
                bodyStyle={{ paddingTop: 12, paddingBottom: 8 }}
              >
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="列表封面（16:9）"
                      name="poster_url"
                      rules={[{ required: true, message: "请上传列表封面（16:9）" }]}
                      extra="请上传16:9横版（推荐1920x1080），用于列表与横版展示"
                    >
                      <PosterUpload
                        movieId={movieId}
                        usageType="list"
                        maxSizeMB={10}
                        value={posterUrl}
                        onChange={(url) => {
                          setPosterUrl(url || undefined);
                          form.setFieldValue("poster_url", url);
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="竖版背景（9:16，详情页）"
                      name="backdrop_url"
                      extra="可选，建议9:16（推荐1080x1920）；缺省时 APP 端会用横图自动适配"
                    >
                      <PosterUpload
                        movieId={movieId}
                        usageType="detail"
                        maxSizeMB={12}
                        value={backdropUrl}
                        onChange={(url) => {
                          setBackdropUrl(url || undefined);
                          form.setFieldValue("backdrop_url", url);
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>

              <Card title="视频上传" bordered={false} bodyStyle={{ paddingTop: 12, paddingBottom: 8 }}>
                <Form.Item
                  label="视频文件"
                  extra="请上传影片视频文件，大小上限5GB，上传成功后才能提交"
                  rules={[{ required: true, message: "请上传视频文件" }]}
                >
                  <VideoUpload
                    movieId={movieId}
                    maxSizeGB={5}
                    maxDurationSeconds={7200}
                    value={videoUid}
                    onChange={(uid) => setVideoUid(uid)}
                  />
                </Form.Item>
              </Card>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
                <Button onClick={() => setCurrentStep(0)}>上一步</Button>
                <Button
                  type="primary"
                  onClick={handleSaveMedia}
                  loading={savingMedia}
                  disabled={!movieId}
                >
                  保存封面与视频
                </Button>
              </div>
            </Space>
          )}
        </Form>
      </Space>
    </Edit>
  );
};
